/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import { Fade } from 'react-awesome-reveal';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NotFoundPage from 'pages/NotFoundPage';
import Seo from 'parts/Seo';

export default function BlogDetail({ data }) {
  const { id } = useParams();
  const detailData = data.find((item) => item.id === id);

  if (!detailData) {
    return <NotFoundPage />;
  }

  // Generate related articles (exclude current article)
  const relatedArticles = data.filter((item) => item.id !== id).slice(0, 3);

  return (
    <article className="container mx-auto pt-24 pb-20 px-4">
      <Seo
        title={detailData.title}
        description={detailData.summary}
        image={detailData.imageUrl}
        type="article"
        canonicalUrl={`https://teknisi.space/blog/${id}`}
        keywords={`${detailData.title}, teknisi cnc, service cnc, artikel cnc, blog teknisi, panduan mesin cnc`}
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "${detailData.title}",
              "description": "${detailData.summary}",
              "image": "${detailData.imageUrl}",
              "author": {
                "@type": "Person",
                "name": "${detailData.author}"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Teknisi CNC",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://teknisi.space/logo192.png"
                }
              },
              "datePublished": "${detailData.date}",
              "dateModified": "${detailData.date}",
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://teknisi.space/blog/${id}"
              }
            }
          `}
        </script>
      </Helmet>
      <Fade bottom>
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb Navigation */}
          <nav className="mb-6 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-gray-500">
              <li><Link to="/" className="hover:text-theme-purple">Beranda</Link></li>
              <li>/</li>
              <li><Link to="/blog" className="hover:text-theme-purple">Blog</Link></li>
              <li>/</li>
              <li className="text-theme-blue font-medium truncate max-w-xs">{detailData.title}</li>
            </ol>
          </nav>

          <header className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold text-theme-purple bg-purple-100 px-3 py-1 rounded-full mb-4">
              Info CNC
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-theme-blue mb-4 leading-tight">
              {detailData.title}
            </h1>
            <div className="flex justify-center items-center gap-4 text-gray-500 text-sm">
              <time dateTime={detailData.date}>{detailData.date}</time>
              <span>•</span>
              <span>Oleh {detailData.author}</span>
            </div>
          </header>

          <figure className="w-full h-64 md:h-96 mb-10 overflow-hidden rounded-xl shadow-lg">
            <img
              src={detailData.imageUrl}
              alt={detailData.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </figure>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
            {detailData.content.split('\n').map((paragraph, index) => (
              paragraph.trim() && (
                // eslint-disable-next-line react/no-array-index-key
                <p key={index} className="mb-5 text-base md:text-lg">
                  {paragraph}
                </p>
              )
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-12 p-6 bg-gradient-to-r from-theme-purple/10 to-theme-blue/10 rounded-xl text-center">
            <h3 className="text-xl font-bold text-theme-blue mb-2">Butuh Bantuan Teknisi CNC?</h3>
            <p className="text-gray-600 mb-4">Tim teknisi profesional kami siap membantu Anda dengan layanan terbaik.</p>
            <Link 
              to="/#discuss" 
              className="inline-block bg-theme-purple text-white px-6 py-3 rounded-lg font-medium hover:bg-dark-theme-purple transition-colors"
            >
              Hubungi Kami
            </Link>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-theme-blue mb-6">Artikel Terkait</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((article) => (
                  <Link 
                    key={article.id} 
                    to={`/blog/${article.id}`}
                    className="group border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="h-32 overflow-hidden">
                      <img 
                        src={article.imageUrl} 
                        alt={article.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-theme-blue group-hover:text-theme-purple transition-colors line-clamp-2 text-sm">
                        {article.title}
                      </h3>
                      <p className="text-gray-500 text-xs mt-2">{article.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </Fade>
    </article>
  );
}
